import { BotaoLink } from '../../../components/Botao'
import { IconeAlerta } from '../../../components/Icones'
import type { MesSemOrdem } from '../../../types/ordemDeServico'
import { OS } from '../textosDaOs'

/**
 * Os meses cobertos por este sistema que não têm ordem de serviço registrada.
 *
 * **É a faixa que abre a tela, e é o maior valor isolado desta entrega.** Ausência de registro não
 * gera linha, não gera alerta e não gera número — ela só existe se alguém for procurar. A faixa é
 * esse alguém.
 *
 * **A palavra "atrasada" não aparece, e isso é regra e não estilo.** O TR manda emitir a ordem "no
 * início de cada mês" (4.1.2.3.13, p.16; 4.1.2.4.7, p.17) e não escreve até que dia — por isso o
 * parâmetro `os.prazo_de_emissao_mensal` está INDETERMINADO. Chamar de atraso o que o contrato não
 * datou seria criar obrigação que ele não criou, e quem pagaria por ela é a contratada. O que o
 * sistema pode afirmar é o que ele viu: não há registro.
 *
 * **Os meses NÃO vêm da tabela de competências, nem do começo do contrato.** Da tabela seria pior:
 * ela tem zero linhas em produção, e a faixa nasceria vazia dizendo em silêncio que está tudo em
 * dia — exatamente ao contrário. E do começo do contrato seria errado na direção oposta: o contrato
 * vale desde 06/05/2026, mas a fiscalização só passou a trabalhar aqui dentro em agosto, e maio,
 * junho e julho foram medidos à mão. Apontá-los seria três meses de falso buraco no primeiro dia de
 * uso — e aviso que nasce errado ensina a ignorar o aviso. O servidor resolve isso em
 * `pendenciasDeEmissao()`, a partir de `app.contrato.primeira-competencia-no-sistema`.
 *
 * @param props os meses descobertos e se quem está vendo pode registrar
 * @returns a faixa, ou a linha de que não há mês descoberto
 */
export function MesesSemOrdem({ meses, podeRegistrar }: { meses: MesSemOrdem[]; podeRegistrar: boolean }) {
  if (meses.length === 0) {
    return (
      <div className="text-sm text-slate-600">
        <p>{OS.mesesSemOrdem.nenhum}</p>
        <p className="mt-1 text-xs text-slate-500">{OS.mesesSemOrdem.antesDoSistema}</p>
      </div>
    )
  }

  return (
    <section
      aria-label={OS.mesesSemOrdem.titulo(meses.length)}
      className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4"
    >
      <IconeAlerta className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-amber-900">{OS.mesesSemOrdem.titulo(meses.length)}</h2>
        <p className="mt-1 max-w-prose text-sm text-amber-900">{OS.mesesSemOrdem.ajuda}</p>
        <p className="mt-1 max-w-prose text-xs text-amber-800">
          {OS.mesesSemOrdem.antesDoSistema}
        </p>

        <ul className="mt-3 flex flex-wrap gap-2">
          {meses.map((mes) => (
            <li key={mes.competencia}>
              {podeRegistrar ? (
                // O caminho do registro já leva o mês: o formulário tem vinte e quatro campos, e
                // fazer a pessoa reescrever o único que a faixa acabou de dizer é como se digita
                // 2026-07 no mês de agosto.
                <BotaoLink to={`/ordens-servico/nova?competencia=${mes.competencia}`} tamanho="pequeno">
                  {OS.mesesSemOrdem.registrarDe(mes.rotulo)}
                </BotaoLink>
              ) : (
                <span className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-300">
                  {mes.rotulo}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
